import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { getActivityReportAPI, getActivityAPI } from "./services/driveService";
import useAuth from "./useAuth";

const ActivityContext = createContext({});

export const ActivityProvider = ({ children }) => {
    const { user } = useAuth();
    const [reportData, setReportData] = useState(null);
    const [activities, setActivities] = useState([]);
    const [loadingReport, setLoadingReport] = useState(false);
    const [loadingActivities, setLoadingActivities] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const refreshActivityReport = useCallback(async (userId = null) => {
        if (!user?.id) return;
        setLoadingReport(true);
        try {
            // Use provided userId or default to current user
            const targetUserId = (userId !== null && userId !== undefined) ? userId : user.id;
            const data = await getActivityReportAPI(targetUserId);
            setReportData(data);
        } catch (error) {
            console.error("Failed to refresh activity report", error);
        } finally {
            setLoadingReport(false);
        }
    }, [user?.id]);

    const fetchActivityLogs = useCallback(async (params = {}) => {
        if (!user?.id) return;
        setLoadingActivities(true);
        try {
            // Allow overriding filterUserId from params, default to current user.id if not present
            const filterUserId = params.hasOwnProperty('filterUserId') ? params.filterUserId : user.id;
            const response = await getActivityAPI({ ...params, filterUserId });
            setActivities(response.data || []);
            setTotalRecords(response.totalRecords || 0);
            setTotalPages(response.totalPages || 1);
            return response;
        } catch (error) {
            console.error("Failed to fetch activities", error);
            throw error;
        } finally {
            setLoadingActivities(false);
        }
    }, [user?.id]);

    // Initial load of report for sidebar/history summary
    useEffect(() => {
        if (user?.id) {
            refreshActivityReport();
        }
    }, [user?.id, refreshActivityReport]);

    const value = {
        reportData,
        activities,
        loadingReport,
        loadingActivities,
        totalRecords,
        totalPages,
        refreshActivityReport,
        fetchActivityLogs
    };

    return (
        <ActivityContext.Provider value={value}>
            {children}
        </ActivityContext.Provider>
    );
};

export const useActivity = () => {
    const context = useContext(ActivityContext);
    if (!context) {
        throw new Error("useActivity must be used within an ActivityProvider");
    }
    return context;
};
